(() => {
  class YPBPurchaseModule extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ypbReady === 'true') return;
      this.dataset.ypbReady = 'true';

      this.productId = String(this.dataset.productId || '');
      this.scope = this.parentElement || document;
      this.selectedQuantity = Number(this.dataset.defaultQuantity) || 1;
      this.currentPurchase = 'one_time';
      this.currentVariantId = String(this.dataset.initialVariantId || '');
      this.quantityButtons = Array.from(this.querySelectorAll('[data-ypb-quantity]'));
      this.purchaseButtons = Array.from(this.querySelectorAll('[data-ypb-purchase-type]'));
      this.planButtons = Array.from(this.querySelectorAll('[data-ypb-selling-plan]'));
      this.frequencyPanel = this.querySelector('[data-ypb-frequency]');
      this.activePlanId = this.planButtons[0]?.dataset.ypbSellingPlan || '';
      this.pricing = this.readPricing();
      this.hiddenLegacyElements = new Set();
      this.handleScopeChange = this.onScopeChange.bind(this);

      this.bindControls();
      this.scope.addEventListener('change', this.handleScopeChange);
      this.revealNativeProductForms();
      this.hideNativeQuantity();
      this.suppressSealUI();
      this.updateCurrentVariantFromNativeForm();
      this.updatePlanSavings();
      this.applyInitialState();

      this.observer = new MutationObserver(() => {
        this.revealNativeProductForms();
        this.hideNativeQuantity();
        this.suppressSealUI();
        this.updateCurrentVariantFromNativeForm();
        this.syncNativeForms();
      });
      this.observer.observe(this.scope, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.scope?.removeEventListener('change', this.handleScopeChange);
      this.hiddenLegacyElements?.forEach((element) => element.classList.remove('ypb-native-control-hidden'));
      this.hiddenLegacyElements?.clear();
    }

    readPricing() {
      try {
        return JSON.parse(this.querySelector('[data-ypb-pricing]')?.textContent || '{}');
      } catch (error) {
        console.warn('YPB Purchase Module pricing data could not be read.', error);
        return {};
      }
    }

    bindControls() {
      this.quantityButtons.forEach((button) => {
        button.addEventListener('click', () => this.selectQuantity(Number(button.dataset.ypbQuantity) || 1));
      });

      this.purchaseButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          if (button.hasAttribute('data-ypb-subscription-toggle')) {
            this.selectPurchaseType(this.currentPurchase === 'subscription' ? 'one_time' : 'subscription');
          } else {
            this.selectPurchaseType(button.dataset.ypbPurchaseType);
          }
        });
      });

      this.planButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.activePlanId = String(button.dataset.ypbSellingPlan || '');
          this.selectPurchaseType('subscription');
          this.applyActivePlanState();
          this.syncNativeForms();
          this.updatePrices();
        });
      });
    }

    applyInitialState() {
      this.selectQuantity(this.selectedQuantity);
      const wantsSubscription = this.dataset.defaultPurchase === 'subscription' && this.planButtons.length > 0;
      this.selectPurchaseType(wantsSubscription ? 'subscription' : 'one_time');
    }

    selectQuantity(quantity) {
      this.selectedQuantity = Math.max(1, Number(quantity) || 1);
      this.quantityButtons.forEach((button) => {
        const selected = Number(button.dataset.ypbQuantity) === this.selectedQuantity;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.style.borderWidth = selected ? '2px' : '';
        button.style.boxShadow = selected ? '0 0 0 1px var(--ypb-accent)' : '';
      });
      this.syncNativeForms();
      this.updatePrices();
    }

    selectPurchaseType(type) {
      const subscriptionAvailable = this.planButtons.length > 0;
      this.currentPurchase = type === 'subscription' && subscriptionAvailable ? 'subscription' : 'one_time';
      this.dataset.currentPurchase = this.currentPurchase;

      this.purchaseButtons.forEach((button) => {
        const selected = this.currentPurchase === 'subscription'
          ? button.dataset.ypbPurchaseType === 'subscription'
          : button.dataset.ypbPurchaseType === 'one_time';
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      if (this.currentPurchase === 'subscription') {
        if (!this.activePlanId && this.planButtons[0]) this.activePlanId = String(this.planButtons[0].dataset.ypbSellingPlan || '');
        this.applyActivePlanState();
      }

      if (this.frequencyPanel) {
        const always = this.dataset.subscriptionDisplay === 'always';
        this.frequencyPanel.hidden = !always && this.currentPurchase !== 'subscription';
        this.frequencyPanel.classList.toggle('is-inactive', always && this.currentPurchase !== 'subscription');
      }

      this.syncNativeForms();
      this.updatePrices();
    }

    applyActivePlanState() {
      this.planButtons.forEach((button) => {
        const selected = String(button.dataset.ypbSellingPlan) === String(this.activePlanId);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    findNativeProductForms() {
      if (!this.scope?.querySelectorAll) return [];
      return Array.from(this.scope.querySelectorAll('product-form[data-product-id]')).filter((productForm) => {
        if (this.contains(productForm) || productForm.closest('ypb-purchase-module')) return false;
        return String(productForm.dataset.productId || '') === this.productId;
      });
    }

    revealNativeProductForms() {
      this.findNativeProductForms().forEach((productForm) => {
        productForm.classList.remove('ypb-native-control-hidden');
        this.hiddenLegacyElements.delete(productForm);
      });
    }

    hideNativeQuantity() {
      if (!this.scope?.querySelectorAll) return;
      this.scope.querySelectorAll('quantity-input[data-product-id]').forEach((control) => {
        if (String(control.dataset.productId || '') !== this.productId) return;
        control.classList.add('ypb-native-control-hidden');
        this.hiddenLegacyElements.add(control);
      });
    }

    suppressSealUI() {
      if (!this.scope?.querySelectorAll) return;
      this.scope.querySelectorAll('.sealsubs-target-element, .sealsubs-container').forEach((element) => {
        if (element.querySelector?.(`product-form[data-product-id="${this.productId}"]`)) return;
        element.classList.add('ypb-native-control-hidden');
        this.hiddenLegacyElements.add(element);
      });
    }

    syncNativeForms() {
      this.findNativeProductForms().forEach((productForm) => {
        const form = productForm.querySelector('form');
        if (!form) return;

        let quantityInput = form.querySelector('input[name="quantity"]');
        if (!quantityInput) {
          quantityInput = document.createElement('input');
          quantityInput.type = 'hidden';
          quantityInput.name = 'quantity';
          form.appendChild(quantityInput);
        }
        quantityInput.value = String(this.selectedQuantity);
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

        let sellingPlanInput = form.querySelector('input[name="selling_plan"][data-ypb-controlled-selling-plan]');
        if (!sellingPlanInput) {
          sellingPlanInput = document.createElement('input');
          sellingPlanInput.type = 'hidden';
          sellingPlanInput.name = 'selling_plan';
          sellingPlanInput.dataset.ypbControlledSellingPlan = 'true';
          form.appendChild(sellingPlanInput);
        }

        form.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
          if (input !== sellingPlanInput) input.disabled = true;
        });

        if (this.currentPurchase === 'subscription' && this.activePlanId) {
          sellingPlanInput.value = String(this.activePlanId);
          sellingPlanInput.disabled = false;
        } else {
          sellingPlanInput.value = '';
          sellingPlanInput.disabled = true;
        }
        sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateCurrentVariantFromNativeForm() {
      const nativeForm = this.findNativeProductForms()[0];
      const variantId = nativeForm?.querySelector('form input[name="id"]')?.value;
      if (variantId) this.currentVariantId = String(variantId);
    }

    onScopeChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.name !== 'id') return;
      const productForm = target.closest('product-form[data-product-id]');
      if (!productForm || String(productForm.dataset.productId || '') !== this.productId) return;
      this.currentVariantId = String(target.value || this.currentVariantId);
      this.updatePlanSavings();
      this.updatePrices();
      this.syncNativeForms();
    }

    getVariantPricing() {
      return this.pricing?.variants?.[this.currentVariantId]
        || this.pricing?.variants?.[String(this.dataset.initialVariantId || '')]
        || Object.values(this.pricing?.variants || {})[0]
        || { regularUnit: 0, plans: {} };
    }

    calculate(quantity) {
      const variantPricing = this.getVariantPricing();
      const regularUnit = Number(variantPricing.regularUnit || 0);
      const regularTotal = regularUnit * quantity;
      let currentTotal = regularTotal;

      if (this.currentPurchase === 'subscription' && this.activePlanId) {
        const planUnit = Number(variantPricing.plans?.[this.activePlanId] || regularUnit);
        currentTotal = planUnit * quantity;
      } else if (quantity >= 2 && this.pricing.applyBundlePreview) {
        const defaultPercent = Number(this.pricing.bundlePercent || 0);
        const percent = Math.max(0, Math.min(100, quantity === 3 ? 30 : defaultPercent));
        currentTotal = Math.round(regularTotal * (100 - percent) / 100);
      }

      return {
        regularUnit,
        regularTotal,
        currentTotal,
        currentUnit: quantity > 0 ? Math.round(currentTotal / quantity) : currentTotal,
        saving: Math.max(0, regularTotal - currentTotal)
      };
    }

    updatePrices() {
      const variantPricing = this.getVariantPricing();
      if (!Number(variantPricing.regularUnit || 0)) return;
      const style = this.dataset.priceStyle || 'total';

      this.quantityButtons.forEach((button) => {
        const quantity = Number(button.dataset.ypbQuantity) || 1;
        const price = this.calculate(quantity);
        const currentNode = button.querySelector('[data-ypb-current-price]');
        const compareNode = button.querySelector('[data-ypb-compare-price]');
        const savingNode = button.querySelector('[data-ypb-saving]');

        if (currentNode) currentNode.textContent = this.formatMoney(style === 'per_bottle' ? price.currentUnit : price.currentTotal);
        if (compareNode) {
          compareNode.textContent = price.saving > 0 ? this.formatMoney(price.regularTotal) : '';
          compareNode.hidden = price.saving <= 0;
        }
        if (savingNode) {
          savingNode.textContent = price.saving > 0 ? `Save ${this.formatMoney(price.saving)}` : '';
          savingNode.hidden = price.saving <= 0;
        }
      });
    }

    updatePlanSavings() {
      const variantPricing = this.getVariantPricing();
      const regularUnit = Number(variantPricing.regularUnit || 0);
      if (!regularUnit) return;
      this.planButtons.forEach((button) => {
        const planId = String(button.dataset.ypbSellingPlan || '');
        const planUnit = Number(variantPricing.plans?.[planId] || regularUnit);
        const percent = Math.max(0, Math.round((1 - (planUnit / regularUnit)) * 100));
        const node = button.querySelector('[data-ypb-plan-saving]');
        if (node) node.textContent = percent > 0 ? `Save ${percent}%` : '';
      });
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || 'AUD';
      const locale = this.dataset.locale || undefined;
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }
  }

  if (!customElements.get('ypb-purchase-module')) customElements.define('ypb-purchase-module', YPBPurchaseModule);
})();
